import {useWorkspace} from "~/contexts/workspaceContext";
import {
    type WorkspaceCapability,
    WorkspaceCapabilityStatus,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type {
    GetWorkspaceCapabilityConfigurationType,
    WorkspaceCapabilityType,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import {
    WorkspaceUiConfigStatus,
    type WorkspaceUiConfig,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceUiConfig";
import type {WorkspaceUiConfigConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceUiConfigConfiguration";
import type {WorkspaceMobileNavigationConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceMobileBottomCreateItem";
import type {WorkspaceUsersConfiguration} from "@vesta/vos-types/modules/workspaceLevel/users/domain/WorkspaceUsersConfiguration";

type WorkspaceCapabilities = ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>;
type WorkspaceUiConfigs = ReadonlyArray<WorkspaceUiConfig>;

function findEnabledWorkspaceCapability<T extends WorkspaceCapabilityType>(
    workspaceCapabilities: WorkspaceCapabilities,
    type: T,
): WorkspaceCapability<T> | undefined {
    return workspaceCapabilities.find(
        (capability): capability is WorkspaceCapability<T> =>
            capability.type === type && capability.status === WorkspaceCapabilityStatus.enabled,
    );
}

function findEnabledWorkspaceUiConfig(
    workspaceUiConfigs: WorkspaceUiConfigs,
): WorkspaceUiConfig | undefined {
    return workspaceUiConfigs.find((config) => config.status === WorkspaceUiConfigStatus.enabled);
}

export function getOptionalWorkspaceCapabilityConfiguration<T extends WorkspaceCapabilityType>(
    workspaceCapabilities: WorkspaceCapabilities,
    type: T,
): GetWorkspaceCapabilityConfigurationType<T> | null {
    return findEnabledWorkspaceCapability(workspaceCapabilities, type)?.configuration ?? null;
}

export function getOptionalWorkspaceUiConfigConfiguration(
    workspaceUiConfigs: WorkspaceUiConfigs,
): WorkspaceUiConfigConfiguration | null {
    return findEnabledWorkspaceUiConfig(workspaceUiConfigs)?.configuration ?? null;
}

export function getRequiredWorkspaceCapabilityConfiguration<T extends WorkspaceCapabilityType>(
    workspaceCapabilities: WorkspaceCapabilities,
    type: T,
): GetWorkspaceCapabilityConfigurationType<T> {
    const configuration = getOptionalWorkspaceCapabilityConfiguration(workspaceCapabilities, type);
    if (configuration === null) {
        throw new Error(`Workspace capability not found/enabled for type ${type}`);
    }
    return configuration;
}

export function useOptionalWorkspaceCapabilityConfiguration<T extends WorkspaceCapabilityType>(
    type: T,
): GetWorkspaceCapabilityConfigurationType<T> | null {
    const workspaceContext = useWorkspace();
    const configuration = getOptionalWorkspaceCapabilityConfiguration(workspaceContext.workspaceCapabilities, type);
    if (configuration === null) {
        return null;
    }
    return configuration;
}

export function useRequiredWorkspaceCapabilityConfiguration<T extends WorkspaceCapabilityType>(
    type: T,
): GetWorkspaceCapabilityConfigurationType<T> {
    const workspaceContext = useWorkspace();
    return getRequiredWorkspaceCapabilityConfiguration(workspaceContext.workspaceCapabilities, type);
}

export function useWorkspaceUsersConfiguration(): WorkspaceUsersConfiguration {
    const {workspaceUiConfig} = useWorkspace();
    return workspaceUiConfig?.configuration.users ?? {};
}

/** Mobile create/home shortcuts configuration. */
export function useOptionalWorkspaceMobileNavigationConfiguration(
    _type?: unknown,
): WorkspaceMobileNavigationConfiguration | null {
    return {};
}

export function useRequiredWorkspaceMobileNavigationConfiguration(
    _type?: unknown,
): WorkspaceMobileNavigationConfiguration {
    return {};
}
